import { useEffect, useState } from 'react';
import { getSocket } from './socket';
import { useAuthStore } from '@/auth/authStore';

export type NetStatus = 'idle' | 'connecting' | 'connected' | 'failed';

interface UseNetOpts {
  /** If true, connect immediately on mount. Defaults to true. */
  autoConnect?: boolean;
  /** If we haven't connected within this many ms, status flips to 'failed' so
   * the caller can fall back to offline mode. Defaults to 3000. */
  failAfterMs?: number;
}

/**
 * Lifecycle wrapper around the singleton Socket.IO client. Each screen calls
 * `useNet()` to ensure the socket is connected before subscribing to events.
 * The hook does NOT disconnect on unmount — the socket is shared across the
 * app and survives navigation.
 */
export function useNet(opts: UseNetOpts = {}): { status: NetStatus; socket: ReturnType<typeof getSocket> } {
  const { autoConnect = true, failAfterMs = 3000 } = opts;
  const token = useAuthStore((s) => s.token);
  const [status, setStatus] = useState<NetStatus>('idle');

  const socket = getSocket({ token: token ?? undefined });

  useEffect(() => {
    if (!autoConnect) return;

    if (socket.connected) {
      setStatus('connected');
      return;
    }

    setStatus('connecting');
    let failed = false;

    const onConnect = () => {
      if (failed) return;
      setStatus('connected');
    };
    const onConnectError = (err: Error) => {
      // Auth-level rejection: token is bad → drop session so the user is
      // forced to re-login instead of looping forever on connect retries.
      const msg = String(err?.message ?? err);
      if (msg === 'BAD_TOKEN' || msg === 'NO_TOKEN' || msg === 'USER_NOT_FOUND') {
        useAuthStore.getState().clear();
      }
    };
    const onDisconnect = () => {
      setStatus('connecting');
    };

    socket.on('connect', onConnect);
    socket.on('connect_error', onConnectError);
    socket.on('disconnect', onDisconnect);

    if (!socket.connected) socket.connect();

    const failTimer = window.setTimeout(() => {
      if (!socket.connected) {
        failed = true;
        setStatus('failed');
      }
    }, failAfterMs);

    return () => {
      socket.off('connect', onConnect);
      socket.off('connect_error', onConnectError);
      socket.off('disconnect', onDisconnect);
      window.clearTimeout(failTimer);
    };
  }, [autoConnect, failAfterMs, socket]);

  return { status, socket };
}
